<script setup>
import { ref, computed, onMounted } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import EmailMarketingTemplatesAPI from 'dashboard/api/emailMarketingTemplates';

const emit = defineEmits(['apply']);

const templates = ref([]);
const isOpen = ref(false);
const filterText = ref('');
const isLoading = ref(false);

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await EmailMarketingTemplatesAPI.list({
      context: 'inbox',
    });
    templates.value = data || [];
  } catch {
    templates.value = [];
  } finally {
    isLoading.value = false;
  }
};

const filtered = computed(() => {
  if (!filterText.value) return templates.value;
  const q = filterText.value.toLowerCase();
  return templates.value.filter(
    t =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.category || '').toLowerCase().includes(q)
  );
});

const toggle = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) load();
};

const close = () => {
  isOpen.value = false;
  filterText.value = '';
};

const apply = template => {
  emit('apply', template);
  close();
};

onMounted(() => {
  // Pré-carrega pra mostrar contagem se quiser fazer badge no botão.
  load();
});
</script>

<template>
  <div v-on-click-outside="close" class="relative">
    <button
      v-tooltip.top="$t('EMAIL_MKT.REPLYBOX.PICK_TEMPLATE_TOOLTIP')"
      type="button"
      class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-n-slate-12 hover:bg-n-slate-3 rounded-md"
      @click="toggle"
    >
      <span class="i-lucide-layers size-3.5" />
      {{ $t('EMAIL_MKT.REPLYBOX.PICK_TEMPLATE') }}
      <span v-if="templates.length" class="text-[10px] text-n-slate-10 px-1">
        {{ templates.length }}
      </span>
    </button>

    <div
      v-if="isOpen"
      class="absolute z-50 top-full left-0 mt-1 w-80 bg-n-background border border-n-weak rounded-lg shadow-xl flex flex-col max-h-80"
    >
      <div class="p-2 border-b border-n-weak">
        <input
          v-model="filterText"
          type="text"
          :placeholder="$t('EMAIL_MKT.REPLYBOX.SEARCH_PLACEHOLDER')"
          class="reset-base w-full text-xs border border-n-weak rounded-md px-2 py-1.5 bg-n-background text-n-slate-12"
        />
      </div>
      <div class="overflow-y-auto flex-1">
        <div v-if="isLoading" class="p-4 text-center text-xs text-n-slate-10">
          {{ $t('EMAIL_MKT.REPLYBOX.LOADING') }}
        </div>
        <div
          v-else-if="!filtered.length"
          class="p-4 text-center text-xs text-n-slate-10"
        >
          {{ $t('EMAIL_MKT.REPLYBOX.NO_TEMPLATES') }}
        </div>
        <button
          v-for="t in filtered"
          :key="t.id"
          type="button"
          class="w-full text-left px-3 py-2 hover:bg-n-slate-2 border-b border-n-weak last:border-0"
          @click="apply(t)"
        >
          <div class="text-xs font-medium text-n-slate-12">{{ t.name }}</div>
          <div v-if="t.category" class="text-[10px] text-n-slate-10 mt-0.5">
            {{ t.category }}
          </div>
        </button>
      </div>
    </div>
  </div>
</template>
